import React, { useEffect } from 'react';
import { X, User, Mail, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LogoutButton } from './LogoutButton';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const { username, userEmail } = useAuth();

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/70 z-[100]"
      onClick={onClose}
    >
      <div 
        className="absolute top-16 right-4 bg-[#333333] rounded-xl w-72 p-4 shadow-2xl border border-gray-600"
        style={{ 
          animation: 'fadeInSlideDown 0.2s ease-out',
          transformOrigin: 'top right'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Profile</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1"
            aria-label="Close modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Profile Content */}
        <div className="space-y-3 mb-4">
          {/* Username */}
          <div className="flex items-center gap-2.5 p-3 bg-gray-700/50 rounded-lg">
            <div className="w-8 h-8 bg-[#5CD2F4] rounded-full flex items-center justify-center flex-shrink-0">
              <User size={16} className="text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400">Username</p>
              <p className="font-medium text-white text-sm truncate">{username || 'User'}</p>
            </div>
          </div>

          {/* Email */}
          <div className="flex items-center gap-2.5 p-3 bg-gray-700/50 rounded-lg">
            <div className="w-8 h-8 bg-[#5CD2F4] rounded-full flex items-center justify-center flex-shrink-0">
              <Mail size={16} className="text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400">Email</p>
              <p className="font-medium text-white text-sm truncate">{userEmail || 'Not set'}</p>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <LogoutButton className="w-full bg-gray-700 hover:bg-gray-600 text-red-400 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-sm">
          <LogOut size={18} />
          Logout
        </LogoutButton>
      </div>
    </div>
  );
};

